import React, { useRef, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretLeft, faCaretRight } from "@fortawesome/free-solid-svg-icons";

const NextArrow = ({ onClick }) => (
  <div className="slick-custom-arrow slick-next" onClick={onClick}>
    <FontAwesomeIcon icon={faCaretRight} />
  </div>
);

const PrevArrow = ({ onClick }) => (
  <div className="slick-custom-arrow slick-prev" onClick={onClick}>
    <FontAwesomeIcon icon={faCaretLeft} />
  </div>
);

const Section4 = ({ url }) => {
  const sectionRef = useRef(null);
  const [Slick, setSlick] = useState(null);
  const slickLoaded = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      async (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !slickLoaded.current) {
          slickLoaded.current = true;
          const slickModule = await import("react-slick");
          await import("slick-carousel/slick/slick.css");
          await import("slick-carousel/slick/slick-theme.css");
          setSlick(() => slickModule.default);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const settings = {
    dots: true,
    arrows: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    pauseOnHover: true,
    speed: 600,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

 const cases = [
  {
    image: `${url}images/case-studies/qarshi.png`,
    title: "AI Chatbot Automating Customer Query Management",
    tags: ["Healthcare", "Enterprise", "Pakistan"],
    client: "Qarshi Research International (QRI)",
    industry:
      "Healthcare / Research & Laboratory Services | Enterprise, Pakistan's first internationally accredited laboratory",
    problem:
      "High volumes of customer inquiries were handled entirely manually, causing delayed response times, heavy reliance on support staff for repetitive questions, and no customer support availability outside business hours.",
    built:
      "An AI-enabled NLP chatbot trained on QRI's FAQ database, with automated fallback escalation to email channels, continuous learning from unanswered queries, centralized interaction tracking, and 24/7 website integration providing always-on customer support.",
    result:
      "Reduced manual handling of repetitive customer inquiries with routine questions consistently resolved by the chatbot before reaching human support, faster and more consistent response delivery, always-on engagement outside business hours, lower dependency on support staff, and a scalable foundation for advanced AI analytics.",
    quote:
      "ExD helped us introduce an intelligent, automated customer engagement solution that improves responsiveness and accessibility while allowing our teams to focus on more complex, value-driven interactions.",
    quoteAuthor: "Ahmad Iqbal Qarshi, Owner @ QRI",
  },
  {
    image: `${url}images/case-studies/movig.png`,
    title: "AI Sales Automation for Creator-Brand Partnerships",
    tags: ["Digital Media", "Startup", "Global"],
    client: "Movig",
    industry:
      "Digital Media / Creator Economy / Marketing Technology | Growth-stage startup, USA and global reach",
    problem:
      "Content creators and brands relied on manual, fragmented outreach with no structured workflows, limiting the ability to discover, pitch, and manage brand partnerships at scale.",
    built:
      "A full-stack AI-enabled sales automation platform built on Python, Django, React.js, and PostgreSQL with OpenAI GPT-4, Claude AI, and Anthropic AI integrations, automating end-to-end creator-brand discovery, pitching, and deal management workflows.",
    result:
      "Shortened creator-brand engagement cycles by replacing manual outreach with structured automation, increased consistency in campaign execution and content sourcing, and enabled high-volume partnership operations to scale without proportional increases in manual effort or coordination overhead.",
    quote:
      "ExD helped us build a centralized platform that brought structure and automation to partnerships, enabling more consistent revenue opportunities for creators and a smoother sourcing experience for brands.",
    quoteAuthor: "CEO @ Movig",
  },
  {
    image: `${url}images/case-studies/evGo.png`,
    title: "AI-Driven Financial Intelligence at Scale",
    tags: ["Energy", "Enterprise", "USA"],
    client: "EVgo",
    industry:
      "EV Charging Network / Energy Infrastructure | US Enterprise (1,100+ charging stations across 40 states)",
    problem:
      "Manual, spreadsheet-driven financial processes could not scale to validate electricity bills, model site profitability, or plan capital investments reliably across a rapidly expanding nationwide EV charging network.",
    built:
      "A suite of AI-enabled web applications automating bill validation via third-party billing APIs (Genability), AI-driven financial modeling with automated NPV and IRR calculation, and a CAPEX Estimator Module for data-backed investment planning, built on Python, Django, and predictive analytics.",
    result:
      "Full end-to-end automation of electricity bill validation across hundreds of geographically distributed charging sites, material reduction in manual reconciliation effort and operational risk, consistent site-level and portfolio-wide financial visibility, and standardized data-backed CAPEX and OPEX planning for network expansion.",
    quote:
      "ExD helped us replace manual, spreadsheet-driven processes with a more structured financial intelligence platform, enabling clearer insight, better forecasting, and more confident decision-making as we expand.",
    quoteAuthor: "Badar Khan, CEO @ EVgo",
  },
];

  return (
    <section className="home-section-4" ref={sectionRef}>
      <div className="container overflow-hidden">
        <h2 className="s4-heading" data-aos="fade-up">CASE STUDIES</h2>

        <div className="s4-slider-wrapper" data-aos="fade-up" data-aos-delay="150">
          {Slick ? (
            <Slick {...settings}>
              {cases.map((c, idx) => (
                <div key={idx}>
                  <div className="s4-card">

                    {/* LEFT — image */}
                    <div className="s4-card-image">
                      <img src={c.image} alt={c.title} />
                    </div>

                    {/* RIGHT — content */}
                    <div className="s4-card-content">
                      <h3 className="s4-card-title">{c.title}</h3>

                      <div className="s4-tags">
                        {c.tags.map((tag, i) => (
                          <React.Fragment key={i}>
                            <span className="s4-tag">{tag}</span>
                            {i < c.tags.length - 1 && <span className="s4-tag-sep">|</span>}
                          </React.Fragment>
                        ))}
                      </div>

                      <p className="s4-meta"><strong>Client:</strong> {c.client}</p>
                      <p className="s4-meta"><strong>Industry & Size:</strong> {c.industry}</p>
                      <p className="s4-meta"><strong>The Problem:</strong> {c.problem}</p>
                      <p className="s4-meta"><strong>What We Built:</strong> {c.built}</p>
                      <p className="s4-meta"><strong>The Result:</strong> {c.result}</p>

                      <div className="s4-quote-box">
                        <div className="s4-quote-avatar" />
                        <div>
                          <p className="s4-quote-text">"{c.quote}"</p>
                          <p className="s4-quote-author">{c.quoteAuthor}</p>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              ))}
            </Slick>
          ) : (
            <div className="s4-skeleton" />
          )}
        </div>
      </div>
    </section>
  );
};

export default Section4;